import { useCallback, useMemo } from 'react';

import { BotIcon, CornerDownRightIcon, PlayIcon } from 'lucide-react';

import AvatarGroup from '@/components/atoms/Avatar/AvatarGroup/AvatarGroup';
import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useMembers from '@/hooks/useMembers';
import { useRelatedConversations } from '@/modules/humanChat/hooks/useRelatedConversations';
import { Member } from '@/shared/types/member';
import { timeFromNow } from '@/util/dateUtils';
import { getInitials } from '@/util/memberUtils';

type Props = { session_id: string; onOpen?: () => void };

const AgentAvatar = ({ member }: { member: Member | undefined }) => {
  const initials = getInitials(member?.name);

  return (
    <Avatar size="xxs" className="ring-2 ring-background">
      <AvatarImage src={member?.avatar} alt={member?.name} />
      <AvatarFallback size="xxs">{initials}</AvatarFallback>
    </Avatar>
  );
};

const MAX_AVATARS = 3;

export const RelatedConversationsTile = ({ session_id, onOpen }: Props) => {
  const { relatedConversation, sessionContentLoading } = useRelatedConversations(session_id);
  const { getMember } = useMembers();
  const ChannelIcon = relatedConversation?.channel
    ? integrationIconByType[relatedConversation.channel]
    : undefined;
  const lastMessageAt = relatedConversation?.last_message_at;

  const overflowNames = useMemo(() => {
    if (!relatedConversation) {
      return '';
    }
    return relatedConversation.participated_agents
      .slice(MAX_AVATARS - (ChannelIcon ? 1 : 0))
      .map((a) => getMember(a.agent_id)?.name)
      .filter(Boolean)
      .join(', ');
  }, [relatedConversation, ChannelIcon, getMember]);

  const renderOverflowNode = useCallback(
    (n: number) => (
      <InfoTooltip content={overflowNames}>
        <span className="z-10 flex size-4 cursor-default items-center justify-center rounded-full border-2 border-background bg-bg-muted text-[10px] text-foreground-muted">
          +{n}
        </span>
      </InfoTooltip>
    ),
    [overflowNames]
  );

  if (sessionContentLoading) {
    return <Skeleton className="h-9 w-full" />;
  }

  if (!relatedConversation) {
    return null;
  }

  const renderLeadingAvatar = () => {
    if (relatedConversation?.avatar?.author_type === 'brain') {
      return <BotIcon size={16} className="shrink-0 text-foreground-muted" />;
    }
    if (relatedConversation?.avatar?.author_type === 'agent') {
      return <AgentAvatar member={getMember(relatedConversation?.avatar?.src)} />;
    }
    return (
      <Avatar size="xxs">
        <AvatarFallback size="xxs" />
      </Avatar>
    );
  };

  return (
    <li className="flex items-center gap-1.5 py-1 pr-1">
      {renderLeadingAvatar()}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm leading-5 font-normal text-foreground">
          {relatedConversation.last_message}
        </span>
        <span className="text-xs leading-5 font-normal text-foreground-muted">
          {lastMessageAt ? (
            <TimestampTooltip timestamp={lastMessageAt}>
              {timeFromNow(lastMessageAt)}
            </TimestampTooltip>
          ) : (
            '-'
          )}
        </span>
      </div>

      {(relatedConversation.participated_agents.length > 0 || ChannelIcon) && (
        <div className="flex shrink-0 items-center gap-1">
          <CornerDownRightIcon size={14} className="shrink-0 text-foreground-muted" />
          <AvatarGroup max={MAX_AVATARS} className="shrink-0" overflowNode={renderOverflowNode}>
            {ChannelIcon && (
              <div className="flex size-4 items-center justify-center">
                <ChannelIcon size={16} />
              </div>
            )}
            {relatedConversation.participated_agents.map((agent) => (
              <AgentAvatar key={agent.agent_id} member={getMember(agent.agent_id)} />
            ))}
          </AvatarGroup>
        </div>
      )}
      <Button variant="secondary-neutral" size="icon-xss" onClick={onOpen}>
        <PlayIcon size={14} />
      </Button>
    </li>
  );
};
